import type { QueryClient } from '@tanstack/react-query';
import { parseAppPath } from '@/routes';
import {
  buildCampaignsListQueryKey,
  fetchCampaignsList,
  type CampaignsListQueryArgs,
} from '@/hooks/useCampaigns';
import { getCampaign, getTeamsSearch } from '@/api/beatlyApi';
import { getTokenFromStorage, type AuthUser } from '@/utils/auth';
import { readLastCampaignPath } from '@/utils/lastCampaignPath';

const CAMPAIGNS_LIST_PAGE_SIZE = 10;

function isValidCampaignId(id: string | undefined | null): id is string {
  return typeof id === 'string' && id.length >= 8 && id.includes('-');
}

/**
 * Warms the React Query cache for /campaigns by issuing the same reads
 * SeeCampaignsPage triggers on first paint: teams-search (selector), the
 * page-1 default-filter campaigns list, and — when known — the most recently
 * visited campaign detail (clicked from the navbar's "Last campaign"). Each
 * prefetch is a no-op when fresh data already exists.
 */
export function prefetchCampaigns(
  queryClient: QueryClient,
  user: AuthUser | undefined,
): void {
  const token = getTokenFromStorage();
  if (!token || user?.is_client !== true) return;

  void queryClient.prefetchQuery({
    queryKey: ['client', 'teams', 'search'],
    queryFn: () => getTeamsSearch(token),
  });

  const teamScope =
    typeof user.team?.team_id === 'string' ? user.team.team_id.trim() : '';
  if (teamScope) {
    const args: CampaignsListQueryArgs = {
      teamScope,
      pageNumber: 0,
      pageSize: CAMPAIGNS_LIST_PAGE_SIZE,
      filters: { sortBy: 'newest' },
    };
    void queryClient.prefetchQuery({
      queryKey: buildCampaignsListQueryKey(args),
      queryFn: () => fetchCampaignsList(args, token),
    });
  }

  const lastPath = readLastCampaignPath();
  if (lastPath) {
    const { campaignId } = parseAppPath(lastPath);
    if (isValidCampaignId(campaignId)) {
      void queryClient.prefetchQuery({
        queryKey: ['client', 'campaign', campaignId],
        queryFn: () => getCampaign(campaignId, token, 'en'),
      });
    }
  }
}
